"use client";

import { useEffect, useRef } from "react";
import Icon from "@/components/ui/AppIcon";

const reasons = [
  {
    icon: "TruckIcon",
    title: "穩定供應鏈",
    titleEn: "Stable Supply Chain",
    desc: "上海總公司直接對接工廠，確保貨源穩定，交期準時，降低採購風險。",
  },
  {
    icon: "ShieldCheckIcon",
    title: "品質保證",
    titleEn: "Quality Assurance",
    desc: "所有產品均符合香港及國際標準，提供完整品質認證文件。",
  },
  {
    icon: "CurrencyDollarIcon",
    title: "批發優惠價格",
    titleEn: "Wholesale Pricing",
    desc: "直接工廠價格，無中間商差價，為您節省採購成本，提升利潤空間。",
  },
  {
    icon: "ChatBubbleLeftRightIcon",
    title: "即時客戶服務",
    titleEn: "Instant Customer Support",
    desc: "WhatsApp 即時回覆，中英文雙語服務，快速處理您的查詢與訂單。",
  },
  {
    icon: "BuildingOffice2Icon",
    title: "雙城市佈局",
    titleEn: "Dual-City Network",
    desc: "香港與上海雙據點，靈活應對市場需求，為大灣區客戶提供最佳服務。",
  },
  {
    icon: "StarIcon",
    title: "多元產品線",
    titleEn: "Diverse Product Range",
    desc: "涵蓋清潔、個人護理、廚房、辦公等多個品類，一站式採購解決方案。",
  },
];

export default function WhyChooseUs() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("revealed");
            observer.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px" }
    );
    sectionRef.current?.querySelectorAll(".reveal-hidden").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px section-divider" />
        <div className="absolute bottom-0 left-0 right-0 h-px section-divider" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full blur-[150px] opacity-10"
          style={{ background: "radial-gradient(circle, #C41E3A 0%, transparent 70%)" }} />
      </div>

      <div className="container-main relative z-10">
        <div className="text-center mb-14 reveal-hidden">
          <span className="text-xs text-gold-DEFAULT uppercase tracking-widest font-semibold">為何選擇我們</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mt-2 mb-4">
            信悅的核心優勢
          </h2>
          <p className="text-white/50 max-w-xl mx-auto">
            多年行業積累，為香港及大灣區客戶提供可靠的日用品採購解決方案
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reasons.map((r, i) => (
            <div
              key={r.title}
              className={`glass-card rounded-card p-6 hover:border-gold-DEFAULT/30 transition-all duration-400 group reveal-hidden delay-${i + 1}`}
            >
              <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors duration-300">
                <Icon name={r.icon as any} size={22} className="text-primary-light" />
              </div>
              <h3 className="font-serif text-lg font-semibold text-white mb-1">{r.title}</h3>
              <p className="text-[11px] text-gold-DEFAULT/70 mb-3 tracking-wide">{r.titleEn}</p>
              <p className="text-sm text-white/50 leading-relaxed">{r.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}